/**
 * ASC API Service
 *
 * This module provides functions for interacting with the ASC-specific API endpoints.
 */

import { apiUtils } from '../utils/apiUtils';


/**
 * Fetch facility information
 * @returns {Promise<Object>} Facility data
 */
export const getFacility = async () => {
  const response = await apiUtils.get('/api/asc/facilities');
  if (response.success) {
    // The facility endpoint might return facilities array or a single facility
    const { data } = response;
    if (Array.isArray(data)) {
      return data.length > 0 ? data[0] : null;
    }
    return data.facilities ? data.facilities[0] : data;
  }
  throw new Error(response.error || 'Failed to fetch facility');
};

/**
 * Create a new facility
 * @param {Object} facilityData - New facility data
 * @returns {Promise<Object>} Created facility
 */
export const createFacility = async facilityData => {
  const response = await apiUtils.post('/api/asc/facilities', facilityData);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to create facility');
};

/**
 * Update facility information
 * @param {string} facilityId - Facility ID
 * @param {Object} facilityData - Updated facility data
 * @returns {Promise<Object>} Updated facility
 */
export const updateFacility = async (facilityId, facilityData) => {
  if (!facilityId) {
    throw new Error('Facility ID is required to update facility');
  }
  const response = await apiUtils.put(`/api/asc/facilities/${facilityId}`, facilityData);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to update facility');
};

/**
 * Fetch all surgeons
 * @returns {Promise<Array>} List of surgeons
 */
export const getSurgeons = async facilityId => {
  if (!facilityId) {
    throw new Error('Facility ID is required to fetch surgeons');
  }
  const response = await apiUtils.get(`/api/asc/facilities/${facilityId}/surgeons`);
  if (response.success) {
    // Handle different response formats
    // The API returns { surgeons: [...] } format
    if (response.data && response.data.surgeons) {
      return response.data.surgeons;
    }
    // If the response is already an array
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // Default to empty array
    return [];
  }
  throw new Error(response.error || 'Failed to fetch surgeons');
};

/**
 * Fetch a specific surgeon by ID
 * @param {string} id - Surgeon ID
 * @returns {Promise<Object>} Surgeon data
 */
export const getSurgeon = async (facilityId, id) => {
  if (!facilityId) {
    throw new Error('Facility ID is required to fetch surgeon details');
  }
  const response = await apiUtils.get(`/api/asc/facilities/${facilityId}/surgeons/${id}`);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to fetch surgeon');
};

/**
 * Fetch all protocols
 * @returns {Promise<Array>} List of protocols
 */
export const getProtocols = async facilityId => {
  if (!facilityId) {
    throw new Error('Facility ID is required to fetch protocols');
  }
  const response = await apiUtils.get(`/api/asc/facilities/${facilityId}/protocols`);
  if (response.success) {
    // Extract protocols array from response
    return response.data.protocols || response.data || [];
  }
  throw new Error(response.error || 'Failed to fetch protocols');
};

/**
 * Fetch a specific protocol by ID
 * @param {string} id - Protocol ID
 * @returns {Promise<Object>} Protocol data
 */
export const getProtocol = async (facilityId, id) => {
  if (!facilityId) {
    throw new Error('Facility ID is required to fetch protocol details');
  }
  const response = await apiUtils.get(`/api/asc/facilities/${facilityId}/protocols/${id}`);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to fetch protocol');
};

/**
 * Fetch all patients
 * @returns {Promise<Array>} List of patients
 */
export const getPatients = async () => {
  const response = await apiUtils.get('/api/asc/patients');

  if (response.success) {
    // Handle different response formats
    // The API returns { patients: [...] } format
    if (response.data && response.data.patients) {
      return response.data.patients;
    }
    // If the response is already an array
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // If response.data is the patients array directly (from mock)
    if (response.data && typeof response.data === 'object') {
      return response.data.patients || [];
    }
    // Default to empty array
    return [];
  }
  throw new Error(response.error || 'Failed to fetch patients');
};

/**
 * Fetch a specific patient by ID
 * @param {string} id - Patient ID
 * @returns {Promise<Object>} Patient data
 */
export const getPatient = async id => {
  const response = await apiUtils.get(`/api/asc/patients/${id}`);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to fetch patient');
};

/**
 * Fetch all surgeries
 * @returns {Promise<Array>} List of surgeries
 */
export const getSurgeries = async facilityId => {
  if (!facilityId) {
    throw new Error('Facility ID is required to fetch surgeries');
  }
  const response = await apiUtils.get(`/api/asc/facilities/${facilityId}/surgeries`);
  if (response.success) {
    // Extract surgeries array from response
    return response.data.surgeries || response.data || [];
  }
  throw new Error(response.error || 'Failed to fetch surgeries');
};

/**
 * Fetch surgeries for a specific date
 * @param {string} date - Date in ISO format (YYYY-MM-DD)
 * @returns {Promise<Array>} List of surgeries for the specified date
 */
export const getSurgeriesByDate = async (facilityId, date) => {
  if (!facilityId) {
    throw new Error('Facility ID is required to fetch surgeries by date');
  }
  const response = await apiUtils.get(`/api/asc/facilities/${facilityId}/surgeries/date/${date}`);
  if (response.success) {
    // Extract surgeries array from response
    return response.data.surgeries || response.data || [];
  }
  throw new Error(response.error || 'Failed to fetch surgeries by date');
};

/**
 * Fetch a specific surgery by ID
 * @param {string} id - Surgery ID
 * @returns {Promise<Object>} Surgery data
 */
export const getSurgery = async (facilityId, id) => {
  if (!facilityId) {
    throw new Error('Facility ID is required to fetch surgery details');
  }
  const response = await apiUtils.get(`/api/asc/facilities/${facilityId}/surgeries/${id}`);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to fetch surgery');
};

/**
 * Create a new surgery
 * @param {Object} surgeryData - Surgery data
 * @returns {Promise<Object>} Created surgery
 */
export const createSurgery = async (facilityId, surgeryData) => {
  if (!facilityId) {
    throw new Error('Facility ID is required to create a surgery');
  }
  const response = await apiUtils.post(`/api/asc/facilities/${facilityId}/surgeries`, surgeryData);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to create surgery');
};

/**
 * Update an existing surgery
 * @param {string} id - Surgery ID
 * @param {Object} surgeryData - Updated surgery data
 * @returns {Promise<Object>} Updated surgery
 */
export const updateSurgery = async (facilityId, id, surgeryData) => {
  if (!facilityId) {
    throw new Error('Facility ID is required to update a surgery');
  }
  const response = await apiUtils.put(
    `/api/asc/facilities/${facilityId}/surgeries/${id}`,
    surgeryData
  );
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to update surgery');
};

/**
 * Delete a surgery
 * @param {string} id - Surgery ID
 * @returns {Promise<Object>} Response data
 */
export const deleteSurgery = async (facilityId, id) => {
  if (!facilityId) {
    throw new Error('Facility ID is required to delete a surgery');
  }
  const response = await apiUtils.delete(`/api/asc/facilities/${facilityId}/surgeries/${id}`);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to delete surgery');
};

/**
 * Authenticate user with ASC credentials
 * @param {Object} credentials - User credentials
 * @param {string} credentials.username - Username
 * @param {string} credentials.password - Password
 * @returns {Promise<Object>} Authentication data including token and user info
 */
export const login = async credentials => {
  const response = await apiUtils.post('/api/auth/login', credentials);
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'Failed to login');
};

/**
 * Verify current authentication token
 * @returns {Promise<Object>} User data if authenticated
 */
export const verifyAuth = async () => {
  const response = await apiUtils.get('/api/auth/me');
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error || 'No authentication token found');
};

export default {
  getFacility,
  updateFacility,
  getSurgeons,
  getSurgeon,
  getProtocols,
  getProtocol,
  getPatients,
  getPatient,
  getSurgeries,
  getSurgeriesByDate,
  getSurgery,
  createSurgery,
  updateSurgery,
  deleteSurgery,
  login,
  verifyAuth,
};
